import * as EventTracker from '@src/analytics/EventTracker';
import { useEffect, useRef } from 'react';

/** The reel's shape at the moment it was opened. */
export interface ReelEditorCensus {
  section_count: number;
  chapter_count: number;
  disabled_section_count: number;
}

interface UseTrackReelEditorOpenedArgs {
  /** Whether the Reel Editor tab is the active one. */
  open: boolean;
  /**
   * Whether the renderer has a timeline yet. A link straight into the Reel
   * Editor arrives with the tab already active and nothing to count.
   */
  rendererReady: boolean;
  storyId?: string;
  clipId?: string;
  /**
   * The reel's shape, read at the moment the event fires. `undefined` when
   * there is nothing to count yet, which fires nothing.
   */
  census: () => ReelEditorCensus | undefined;
}

/**
 * PLAT-11063 — counts the Reel Editor being opened, once per opening.
 *
 * Deliberately not inside `ReelEditorPanel`: the panel sits in a
 * `ToggableContainer`, so it stays mounted for the life of the clip preview and
 * only its `display` follows the tab. A mount effect there would fire once, on a
 * tab the creator may never open.
 *
 * The only two edges that mean "the creator opened this" are the tab becoming
 * active and the renderer becoming ready. The reel's shape is read *through*
 * `census` at that moment rather than passed in as a value, because the timeline
 * is rebuilt by every structural edit and `clipJson` is replaced by every
 * autosave — either one as a dependency would re-fire "opened" on each edit the
 * creator makes.
 */
export function useTrackReelEditorOpened({
  open,
  rendererReady,
  storyId,
  clipId,
  census,
}: UseTrackReelEditorOpenedArgs): void {
  // Mirrored through a ref rather than listed as a dependency: `census` closes
  // over its caller's render scope, so it is a fresh function every render and
  // depending on it would fire this on each one (CLAUDE.md pitfall 18).
  const read = useRef(census);
  read.current = census;

  useEffect(() => {
    if (!open || !rendererReady) return;
    const counted = read.current();
    if (!counted) return;

    EventTracker.trackReelEditorOpened({
      story_id: storyId,
      clip_id: clipId,
      ...counted,
    });
  }, [open, rendererReady, storyId, clipId]);
}
